import { useMe } from "@nu-assist/shared-fe";
import { Button } from "@nu-assist/ui";
/**
 * /research/imports — the firm-wide Imports list (prototype v37
 * `#page-my-imports`, iteration 1 "Imports go firm-wide", Q27).
 *
 * Firm-wide list with owner attribution: every recruiter's imports, scoped by
 * the "Imported by:" dropdown (default Me). Filters compose (tag chips + search
 * → `tag` / `q`); paginated (default 5/page). Own rows render the "Imported by"
 * cell accent+bold; counts use thousands separators; conflicts pill orange.
 * Row click → Import Detail (subtask 05). Built mock-first against subtask 01's
 * MSW + the ONE shared typed client.
 *
 * The "+ New Import" CTA (subtask 03) opens the wizard at
 * `/research/imports/new`.
 *
 * `?search_id_thrive=<id>` (the My Searches row CTA) narrows the list to one
 * Thrive search. That filter lives in the URL — not in the component state the
 * other filters use — so the deep-link survives a refresh or a shared link, and
 * a removable chip explains why the list is short.
 *
 * The list itself lives in `imports/ImportsListView.tsx` since 2026-08-13 — the
 * Search Detail page's contacts tab renders the same component with the search
 * filter PINNED. What stays here is everything that is about being a page: the
 * URL as the home of the search filter, the one-shot BCT notice arriving via
 * router state, and the "+ New Import" CTA.
 *
 * Tokenized — design-system token utilities only, no inline `style={{}}`,
 * English copy only.
 */
import { Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
// Note: dev's 2026-06-25-left-menu-shrink-and-page-heading epic adopted
// <PageHeading> on this route while it was still the "coming soon" placeholder
// (title + muted description). ImportsPage has since shipped the real firm-wide
// list (this file) with its own <h1> + count badge in ImportsToolbar — no
// muted description text remains, so the legibility fix PageHeading targeted
// doesn't apply here. PageHeading adoption is intentionally NOT carried over
// for this route (see Sub-Task 09 Discovered Insights).

import { type ImportsClient, createImportsClient } from "../../clients/imports.client";
import { ImportsListView } from "./imports/ImportsListView";

const API_BASE = import.meta.env.VITE_API_URL ?? "https://api.local.nu-assist.com";

export interface ImportsPageProps {
  /** Injectable for tests (defaults to the module-shared typed client). */
  client?: ImportsClient;
  /** Injectable caller email for tests; defaults to `useMe()`. */
  meEmailOverride?: string;
}

/**
 * Router `location.state` shape `BctPage.tsx`'s Save action navigates here
 * with (2026-07-16, per-user direction — replaces the old in-place
 * "BCT saved" banner + "View imports →" link on the BCT page itself: Save
 * now takes the user directly HERE, with the confirmation shown on arrival
 * instead of requiring an extra click to leave the BCT page).
 */
export interface BctSavedNotice {
  matchCount: number | null;
  companiesLabel: string;
}

// One client per app session (lazy-resolves `globalThis.fetch` at call time, so
// MSW interception still applies — see the client's note).
const defaultClient = createImportsClient({ apiBase: API_BASE });

/** Trimmed query-string value, or `null` when absent/blank. */
function readParam(params: URLSearchParams, key: string): string | null {
  return params.get(key)?.trim() || null;
}

export function ImportsPage({ client, meEmailOverride }: ImportsPageProps = {}) {
  const me = useMe();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const meEmail = meEmailOverride ?? (me.status === "authenticated" ? me.data.email : "");
  const activeClient = client ?? defaultClient;

  // The one filter that lives in the URL (deep-linkable, per the My Searches
  // CTA). `search_name` is display-only — the filter itself is the id.
  const searchIdThrive = readParam(searchParams, "search_id_thrive");
  const searchNameParam = readParam(searchParams, "search_name");

  // One-shot "BCT saved" notice carried via `navigate(..., { state })` from
  // BctPage.tsx's Save action. Read into local state on mount (NOT read
  // directly off `location.state` in the JSX below) so it survives the
  // `replace` history call that immediately clears it — otherwise the clear
  // would also blank what's on screen this same render.
  const [bctSavedNotice] = useState<BctSavedNotice | null>(
    () => (location.state as { bctSaved?: BctSavedNotice } | null)?.bctSaved ?? null
  );
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once — strips location.state from history so a back/forward or refresh never re-shows a stale notice; location/navigate identity changes every render and would otherwise re-fire this.
  useEffect(() => {
    if (location.state) {
      navigate(location.pathname + location.search, { replace: true, state: null });
    }
  }, []);

  // Arriving with a search filter starts at Everyone: the imports for a search
  // are usually somebody else's (a search has a team), so keeping the default
  // Me scope would land the recruiter on an empty list next to a chip claiming
  // to be filtering — the worst of both readings.
  //
  // Read once on mount, and it must STAY that way: the toolbar's typeahead now
  // adds the param while the page is mounted, so the old "nothing adds the
  // param" justification is gone. What replaced it is that `ImportsListView`
  // owns the widening for the mounted case, keyed on the filter ARRIVING. Do
  // not make this reactive to close that gap — it is already closed, and two
  // owners for one decision is how they start disagreeing.
  //
  // It is ALSO the scope this page MATERIALISES into the URL on arrival — see
  // the effect below.
  const [initialOwner] = useState(() =>
    readParam(new URLSearchParams(location.search), "search_id_thrive") ? "all" : "you"
  );

  // Write the resolved scope into the URL once, when it arrived without one.
  //
  // WHY THIS EXISTS. `initialOwner` is derived from whether `search_id_thrive`
  // is present — and that param is itself persisted, so it can differ between
  // two mounts of the same reader's session. An ABSENT `owner` therefore does
  // not mean the same thing on every mount, and the round trip this feature
  // exists for is what changes it:
  //
  //   1. My Searches CTA → `?search_id_thrive=4003` → this page resolves
  //      "all", and nothing writes it (the widening effect deliberately skips
  //      its mount pass, so it never runs here).
  //   2. The reader clears the search chip. `clearSearchFilter` drops only the
  //      two search params and deliberately LEAVES the scope at Everyone —
  //      snapping back to Me would delete rows they are looking at.
  //   3. They open a contact list and come back. No `search_id_thrive` now, so
  //      `initialOwner` resolves "you", and the absent `owner` reads as Me.
  //
  // The scope silently NARROWS and rows vanish — the precise harm step 2 exists
  // to prevent, one navigation later. `writeOwner` already fixed the widening
  // mirror of this by always writing on a scope CHANGE; this closes the half
  // where the reader never touched the menu at all.
  //
  // Mount-once and `replace`: it records a decision this page has already made,
  // so it is not a navigation and must not be re-derived later (that would make
  // `initialOwner` reactive, which the note above forbids for a different and
  // still-valid reason).
  //
  // ORDERING NOTE — this effect must stay BELOW the `location.state` strip
  // above it, and the dependency is invisible. That effect navigates to
  // `location.pathname + location.search` built from the MOUNT render's
  // closure. Declared first, it runs first and this one then appends `?owner=`
  // to the result. Reverse them and it would navigate back to its stale
  // `location.search` and ERASE the owner this one just wrote. Same hazard
  // `ImportsListView` documents for its own two effects; do not reorder either
  // pair during a tidy-up without deciding which should win.
  //
  // Twice under StrictMode, not once: `[]` deps with no cleanup means the body
  // runs on both passes, and `searchParams` is the same mount closure both
  // times, so the guard is false twice. The write is idempotent and `replace`,
  // so the URL is identical — but "mount-once" here means once per commit
  // pass, and this file has already been bitten by a StrictMode assumption.
  //
  // biome-ignore lint/correctness/useExhaustiveDependencies: mount-once by design — re-running would recompute a scope the reader may since have changed by hand.
  useEffect(() => {
    // Read it the way the HOOK reads it (`get(...)?.trim() || initialOwner`),
    // not with `has`. A blank `?owner=` satisfies `has` but still falls back to
    // the host-derived default, which would leave exactly the ambiguity this
    // effect exists to remove — and `?owner=` is already a supported hostile
    // input with a test of its own.
    if ((searchParams.get("owner") ?? "").trim()) return;
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("owner", initialOwner);
        return next;
      },
      { replace: true }
    );
  }, []);

  // Drop the search filter: strip both params (the chip's label source too).
  // The owner scope is deliberately left where it is — the recruiter can see it
  // says Everyone and change it; silently snapping it back to Me would drop
  // rows they were just looking at. The page reset that used to sit here now
  // rides the view's own "the filter changed" effect.
  //
  // FUNCTIONAL form, like every write in `useImportsListFilters`. The
  // non-functional form builds its params from the RENDER's closure, so two
  // writes in one tick clobber — a debounce flush landing in the same tick as a
  // typeahead pick is narrow but real, and leaving the two writers in this
  // feature on opposite conventions is how the next reader learns the wrong one.
  function clearSearchFilter() {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("search_id_thrive");
        next.delete("search_name");
        return next;
      },
      { replace: true }
    );
  }

  // The exact inverse: the toolbar's searches typeahead writes the SAME two
  // params the My Searches CTA deep-links with. Going through the URL rather
  // than through component state is what keeps the one filter one thing —
  // `SearchFilterChip`, the deep-link and the refresh all keep working with no
  // second code path, and a reader who applies a filter can share what they
  // are looking at. `search_name` is display-only; the filter is the id, so a
  // search with no name still narrows correctly.
  function applySearchFilter(searchId: string, searchName: string | null) {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("search_id_thrive", searchId);
        if (searchName) next.set("search_name", searchName);
        else next.delete("search_name");
        return next;
      },
      { replace: true }
    );
  }

  return (
    <div data-testid="imports-page" className="flex flex-col gap-4 p-8">
      <ImportsListView
        client={activeClient}
        meEmail={meEmail}
        searchIdThrive={searchIdThrive}
        searchName={searchNameParam}
        onClearSearchFilter={clearSearchFilter}
        onApplySearchFilter={applySearchFilter}
        initialOwner={initialOwner}
        // The scope, chips, search box and page ride in the URL on THIS
        // surface. Filter the list, open a contact list, come back — the
        // filters are still applied, and the address bar is shareable.
        // Search Detail's contacts tab deliberately does not opt in.
        urlFilters
        headerAction={
          <Button onClick={() => navigate("/research/imports/new")} data-testid="new-import-cta">
            <Plus className="h-4 w-4" aria-hidden="true" />
            New Import
          </Button>
        }
        notice={
          bctSavedNotice ? (
            <div
              data-testid="bct-saved-notice"
              className="rounded-md border border-green-500/40 bg-green-500/10 px-4 py-3 text-sm text-green-700"
            >
              Company Map saved — <strong>{bctSavedNotice.matchCount ?? "—"}</strong> match
              {(bctSavedNotice.matchCount ?? 0) !== 1 ? "es" : ""} at{" "}
              <strong>{bctSavedNotice.companiesLabel}</strong>. It has been added to your people
              lists below.
            </div>
          ) : null
        }
      />
    </div>
  );
}
